import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons'
import { Alert, Button, Form, Input } from 'antd'
import { memo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { getUserIsAuth } from '../../../../entities/User'
import { StateSchema } from '../../../../app/providers/StoreProvider'
import { useAppDispatch } from '../../../../shared/lib/hooks/useAppDispatch/useAppDispatch'
import { classNames } from '../../../../shared/lib/classNames/classNames'
import { loginByUsername } from '../../model/services'
import cls from './AuthByUsername.module.scss'

interface AuthByUsernameProps {
    className?: string
}

interface LoginFormValues {
    username: string
    password: string
}

const AuthByUsername = memo((props: AuthByUsernameProps) => {
    const { className } = props
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [form] = Form.useForm<LoginFormValues>()

    const isAuth = useSelector(getUserIsAuth)
    const isLoading = useSelector(
        (state: StateSchema) => state.authByUsername.isLoading
    )
    const error = useSelector(
        (state: StateSchema) => state.authByUsername.error
    )

    useEffect(() => {
        if (isAuth) {
            navigate('/', { replace: true })
        }
    }, [isAuth, navigate])

    const onFinish = async (values: LoginFormValues) => {
        const result = await dispatch(loginByUsername(values))

        if (loginByUsername.fulfilled.match(result)) {
            navigate('/', { replace: true })
        }
    }

    return (
        <div className={classNames(cls.AuthByUsername, {}, [className])}>
            <div className={cls.card}>
                <div className={cls.cardTop}>
                    <div className={cls.iconWrap}>
                        <span className={cls.vanIcon}>🚐</span>
                    </div>
                    <h1 className={cls.title}>{t('auth.loginTitle')}</h1>
                    <p className={cls.subtitle}>
                        {t('auth.loginSubtitle', 'Your adventure awaits')}
                    </p>
                </div>

                <div className={cls.cardBody}>
                    {error && (
                        <Alert
                            type="error"
                            message={error}
                            showIcon
                            className={cls.alert}
                        />
                    )}

                    <Form
                        form={form}
                        name="login_form"
                        layout="vertical"
                        onFinish={onFinish}
                    >
                        <Form.Item
                            label={
                                <span className={cls.formLabel}>
                                    {t('auth.username')}
                                </span>
                            }
                            name="username"
                            rules={[
                                {
                                    required: true,
                                    message: t('auth.usernameRequired')
                                }
                            ]}
                        >
                            <Input
                                size="large"
                                placeholder={t('auth.username')}
                                style={{ borderRadius: 12 }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={
                                <span className={cls.formLabel}>
                                    {t('auth.password')}
                                </span>
                            }
                            name="password"
                            rules={[
                                {
                                    required: true,
                                    message: t('auth.passwordRequired')
                                }
                            ]}
                        >
                            <Input.Password
                                size="large"
                                placeholder={t('auth.password')}
                                style={{ borderRadius: 12 }}
                                iconRender={(visible) =>
                                    visible ? (
                                        <EyeTwoTone />
                                    ) : (
                                        <EyeInvisibleOutlined />
                                    )
                                }
                            />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                block
                                loading={isLoading}
                                className={cls.submitBtn}
                            >
                                {t('auth.submit')}
                            </Button>
                        </Form.Item>
                    </Form>
                </div>

                <div className={cls.footer}>{t('Admin panel')}</div>
            </div>
        </div>
    )
})

export default AuthByUsername
